import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  Webhook, Plus, RefreshCw, Trash2, Send, X, Check, Copy,
  ChevronDown, ChevronUp, Eye, EyeOff, Clock
} from "lucide-react";

export default function WebhooksPage() {
  const [hooks, setHooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ url: "", events: [], description: "" });
  const [showLogs, setShowLogs] = useState(null);
  const [showSecret, setShowSecret] = useState({});

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [hRes, eRes, lRes] = await Promise.all([
        api.get("/webhooks"),
        api.get("/webhooks/events"),
        api.get("/webhooks/logs"),
      ]);
      setHooks(hRes.data || []);
      setEvents(eRes.data || []);
      setLogs(lRes.data || []);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const createHook = async () => {
    if (!form.url) { toast.error("URL is required"); return; }
    try {
      await api.post("/webhooks", form);
      toast.success("Webhook created");
      setShowAdd(false);
      setForm({ url: "", events: [], description: "" });
      fetchAll();
    } catch { toast.error("Failed to create"); }
  };

  const deleteHook = async (id) => {
    await api.delete(`/webhooks/${id}`);
    toast.success("Deleted");
    fetchAll();
  };

  const toggleActive = async (hook) => {
    await api.put(`/webhooks/${hook.webhook_id}`, { is_active: !hook.is_active });
    fetchAll();
  };

  const testHook = async (id) => {
    try {
      const res = await api.post(`/webhooks/${id}/test`);
      toast.success("Test payload sent");
      fetchAll();
    } catch { toast.error("Test failed"); }
  };

  const toggleEvent = (evt) => {
    setForm(f => ({
      ...f,
      events: f.events.includes(evt) ? f.events.filter(e => e !== evt) : [...f.events, evt]
    }));
  };

  const copySecret = (secret) => {
    navigator.clipboard.writeText(secret);
    toast.success("Secret copied");
  };

  if (loading) return <div className="p-8 flex items-center justify-center min-h-[400px]"><RefreshCw className="w-5 h-5 animate-spin text-rose-400" /></div>;

  return (
    <div data-testid="webhooks-page" className="p-6 lg:p-8 max-w-[1200px] mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">API & Webhooks</h1>
          <p className="text-sm text-slate-400 mt-1">Push real-time events to external services</p>
        </div>
        <Button size="sm" onClick={() => setShowAdd(true)} className="bg-slate-900 text-white" data-testid="add-webhook-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Webhook
        </Button>
      </div>

      {/* Add Form */}
      {showAdd && (
        <div data-testid="add-webhook-form" className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-['Outfit'] font-semibold text-slate-900">New Webhook Endpoint</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowAdd(false)}><X className="w-4 h-4" /></Button>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Endpoint URL *</label>
            <Input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://your-server.com/webhook" className="border-slate-200" data-testid="webhook-url-input" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Description</label>
            <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. Sync to CRM" className="border-slate-200" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-2 block">Events ({form.events.length} selected)</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {events.map(evt => (
                <button key={evt} onClick={() => toggleEvent(evt)} data-testid={`event-${evt}`}
                  className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all text-left ${form.events.includes(evt) ? "bg-slate-900 text-white border-rose-500" : "bg-white text-slate-600 border-slate-200 hover:border-rose-300"}`}>
                  {evt}
                </button>
              ))}
            </div>
          </div>
          <Button onClick={createHook} className="bg-slate-900 text-white" data-testid="create-webhook-btn">Create Webhook</Button>
        </div>
      )}

      {/* Webhook List */}
      {hooks.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <Webhook className="w-10 h-10 mx-auto mb-2 text-[#D1D1D1]" />
          <p className="text-slate-400">No webhooks configured</p>
          <p className="text-xs text-rose-400 mt-1">Create one to start receiving events</p>
        </div>
      ) : (
        <div className="space-y-4">
          {hooks.map(hook => {
            const hookLogs = logs.filter(l => l.webhook_id === hook.webhook_id);
            return (
              <div key={hook.webhook_id} data-testid={`hook-${hook.webhook_id}`} className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-2 h-2 rounded-full ${hook.is_active ? "bg-emerald-600" : "bg-[#8A8A8A]"}`} />
                        <p className="text-sm font-medium text-slate-900 font-mono truncate">{hook.url}</p>
                      </div>
                      {hook.description && <p className="text-xs text-slate-400 mb-2">{hook.description}</p>}
                      <div className="flex flex-wrap gap-1">
                        {(hook.events || []).map(evt => (
                          <span key={evt} className="text-[10px] bg-[#FAFAFA] text-slate-600 px-2 py-0.5 rounded border border-slate-200">{evt}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={hook.is_active} onCheckedChange={() => toggleActive(hook)} />
                      <Button variant="outline" size="sm" onClick={() => testHook(hook.webhook_id)} className="border-slate-200 text-slate-600" data-testid={`test-hook-${hook.webhook_id}`}>
                        <Send className="w-3 h-3 mr-1" /> Test
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteHook(hook.webhook_id)} className="text-rose-600">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Secret */}
                  <div className="flex items-center gap-2 mt-3 bg-[#FAFAF9] rounded-lg p-2">
                    <span className="text-xs text-slate-400">Secret:</span>
                    <code className="text-xs text-slate-900 font-mono flex-1">{showSecret[hook.webhook_id] ? hook.secret : "••••••••••••••••"}</code>
                    <Button variant="ghost" size="sm" onClick={() => setShowSecret(s => ({ ...s, [hook.webhook_id]: !s[hook.webhook_id] }))} className="p-1 h-auto">
                      {showSecret[hook.webhook_id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => copySecret(hook.secret)} className="p-1 h-auto"><Copy className="w-3 h-3" /></Button>
                  </div>
                </div>

                {/* Delivery Log */}
                {hookLogs.length > 0 && (
                  <div className="border-t border-slate-200">
                    <button onClick={() => setShowLogs(showLogs === hook.webhook_id ? null : hook.webhook_id)} className="w-full px-5 py-2 flex items-center justify-between text-xs text-slate-400 hover:bg-[#FAFAF9]">
                      <span>Delivery Log ({hookLogs.length})</span>
                      {showLogs === hook.webhook_id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                    {showLogs === hook.webhook_id && (
                      <div className="px-5 pb-3 space-y-1">
                        {hookLogs.slice(0, 10).map(l => (
                          <div key={l.log_id} className="flex items-center gap-2 text-xs">
                            <span className={`w-1.5 h-1.5 rounded-full ${l.status === "delivered" ? "bg-emerald-600" : "bg-rose-600"}`} />
                            <span className="text-slate-600">{l.event}</span>
                            <span className="text-slate-400">{l.status_code}</span>
                            <span className="text-slate-400 ml-auto">{new Date(l.created_at).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
